/**
 * React Query hooks for DataSource provider manifests.
 * Fetches enriched manifests with enrollment status.
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type { EnrichedProviderManifest } from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const providerKeys = {
  all: (tenantId: string | undefined) => ['datasource-providers', tenantId] as const,
  list: (tenantId: string | undefined) => [...providerKeys.all(tenantId), 'list'] as const,
};

// =============================================================================
// API Functions
// =============================================================================

async function fetchProviders(): Promise<EnrichedProviderManifest[]> {
  const { data } = await apiClient.get<EnrichedProviderManifest[]>('/api/v1/datasources/providers');
  return data;
}

// =============================================================================
// Query Hooks
// =============================================================================

export function useProviders() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: providerKeys.list(tenantId),
    queryFn: fetchProviders,
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // Manifests rarely change
  });
}
